import { cn } from '@/lib/utils';
import { createFileRoute, Link, useSearch } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState, useRef } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useStrapi } from '@/hooks/useStrapi';
import { ServicesCategoriesCard, ZoneItem } from '@/lib/types';
import { SubNavContainer } from '@/components/sub-nav-container';
import { MultiSelect } from '@opaz/ui-components';
import { ServiceAutocomplete } from '@/components/ServiceAutocomplete';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useIsMobile } from '@/hooks/use-mobile';

const isAllZonesOption = (title: string) => {
  const normalizedTitle = title.trim().toLocaleLowerCase();
  return ['all zones', 'all zone', 'جميع المناطق', 'كل المناطق'].includes(normalizedTitle);
};

export const Route = createFileRoute('/services/services-catalogue/')({
  component: ServicesCataloguePage,
  validateSearch: (search) => ({
    id: typeof search.id === 'string' ? search.id : undefined,
  }),
  context: () => {
    return {
      getBreadcrumbData: () => ({
        id: null,
        translationKey: null,
      }),
    };
  },
  head: () => {
    return {
      meta: [
        {
          title: 'Services Catalogue - OPAZ E-Service Portal',
        },
      ],
    };
  },
});

function ServicesCataloguePage() {
  const { id } = useSearch({ strict: false });
  const { t, i18n } = useTranslation();
  const analytics = useAnalytics();
  const [selectedZones, setSelectedZones] = useState<string[]>([]);
  const router = useRouter();
  const locale = i18n.language;
  const isMobile = useIsMobile();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: zones, isLoading: zonesLoading, error: zonesError } = useStrapi<ZoneItem>('zones', locale);

  // Build dynamic query for filtering by zones
  const buildZoneFilterQuery = (selectedZones: string[]) => {
    let query = '&sort=order:asc'; // Sort service-categories by order

    // Always populate services and their zones
    query += '&populate[0]=services';
    query += '&populate[services][populate]=zones';

    // Add nested sort for services
    query += '&populate[services][sort]=order:asc';

    // Add filters for selected zones if any
    if (selectedZones.length) {
      selectedZones.forEach((zoneId, index) => {
        query += `&populate[services][filters][zones][id][$in][${index}]=${zoneId}`;
      });
    }

    return query;
  };

  useEffect(() => {
    // Re-fetch data when selected zones change
    // This will trigger the API call with updated filters
  }, [selectedZones]);

  const {
    data: servicesCategories,
    isLoading: servicesCategoriesLoading,
    error: servicesCategoriesError,
  } = useStrapi<ServicesCategoriesCard>('service-categories', locale, buildZoneFilterQuery(selectedZones));

  const categoriesWithServices = useMemo(() => {
    return servicesCategories?.filter((category) => category.services.length > 0) || [];
  }, [servicesCategories]);

  const zoneOptions = useMemo(
    () =>
      (zones ?? [])
        .filter((zone) => !isMobile || !isAllZonesOption(zone.title))
        .map((zone) => ({
          label: zone.title,
          value: zone.id,
        })),
    [isMobile, zones],
  );
  const hasSelectableZones = useMemo(() => (zones ?? []).some((zone) => !isAllZonesOption(zone.title)), [zones]);

  const [activeTab, setActiveTab] = useState<string | undefined>(id);
  const previousZonesRef = useRef<string[]>([]);
  const hasTrackedInitialLoad = useRef(false);

  // Track zone_filter_applied when filtered data is available and zones are selected
  useEffect(() => {
    // Skip tracking on initial load when no zones are selected
    if (!hasTrackedInitialLoad.current && selectedZones.length === 0) {
      hasTrackedInitialLoad.current = true;
      return;
    }
    hasTrackedInitialLoad.current = true;

    // Only track if zones are selected and data has loaded
    if (selectedZones.length > 0 && !servicesCategoriesLoading && categoriesWithServices) {
      // Get zone names for the selected zones
      const zoneNames = selectedZones
        .map((zoneId) => zones?.find((z) => z.id === zoneId)?.title)
        .filter(Boolean)
        .join(', ');

      // Calculate total service count from filtered categories
      const totalServiceCount = categoriesWithServices.reduce((total, category) => total + category.services.length, 0);

      // Only track if zones have changed (avoid duplicate tracking)
      const zonesChanged = JSON.stringify(previousZonesRef.current) !== JSON.stringify(selectedZones);
      if (zonesChanged && zoneNames) {
        // Track with zone_filter_applied event (specific event for zone filtering)
        analytics.trackZoneFilterApplied(zoneNames, totalServiceCount);
        previousZonesRef.current = [...selectedZones];
      }
    }
  }, [selectedZones, categoriesWithServices, servicesCategoriesLoading, zones, analytics]);

  const handleZoneChange = (newSelectedZones: string[]) => {
    const previousZones = selectedZones;
    setSelectedZones(newSelectedZones);

    // Track filter apply/clear for generic tracking
    if (newSelectedZones.length > 0) {
      const zoneNames = newSelectedZones
        .map((zoneId) => zones?.find((z) => z.id === zoneId)?.title)
        .filter(Boolean)
        .join(', ');
      analytics.trackFilterApply('zone', zoneNames);
    }

    // Track filter clear when all zones are removed
    if (newSelectedZones.length === 0 && previousZones.length > 0) {
      analytics.trackFilterClear('zone');
      previousZonesRef.current = [];
    }
  };

  useEffect(() => {
    if (!id && servicesCategories && servicesCategories.length > 0) {
      handleTabChange(servicesCategories[0].documentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, servicesCategories]);

  const handleTabChange = (docId: string) => {
    setActiveTab(docId);

    const { pathname, search } = router.state.location;
    const newSearchParams = new URLSearchParams(search as string);
    newSearchParams.set('id', docId);

    router.history.replace(`${pathname}?${newSearchParams.toString()}`);

    const category = servicesCategories?.find((c) => c.documentId === docId);
    if (category) {
      analytics.trackFilterApply('category', category.title);
    }
  };

  return (
    <>
      <SubNavContainer>
        <div className="w-full max-w-container-960">
          <h1 className="text-3xl font-bold mb-5 text-foreground">{t('servicesCatalogue.title')}</h1>
          <ServiceAutocomplete />
        </div>
      </SubNavContainer>

      <main className="max-w-container-960 mx-auto pt-8 pb-20 px-4 lg:px-0">
        <div className="grid-cols-1 md:grid-cols-[304px_1fr] gap-8 hidden md:grid">
          <h2 className="font-bold text-foreground block mb-2">{t('servicesCatalogue.filters')}</h2>
          <div className="flex flex-wrap items-center gap-2 min-h-10" aria-live="polite">
            {selectedZones.map((zoneValue) => {
              const zone = zones?.find((z) => z.id === zoneValue);
              return (
                <span
                  key={zoneValue}
                  className="flex items-center gap-2 bg-muted rounded-full text-base text-link h-10 w-auto max-w-full px-4 whitespace-nowrap"
                >
                  {zone?.title}
                </span>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[304px_1fr] gap-8">
          <aside className="space-y-8" aria-labelledby="filter-heading">
            <div>
              {zonesLoading ? (
                <div className="h-10 rounded-md bg-muted animate-pulse w-full" />
              ) : zonesError ? (
                <p className="text-sm text-red-500">{t('common.errorMessage')}</p>
              ) : !hasSelectableZones ? null : (
                <MultiSelect
                  options={zoneOptions}
                  onValueChange={handleZoneChange}
                  defaultValue={selectedZones}
                  aria-label={t('servicesCatalogue.zoneLabel')}
                  texts={{
                    label: t('servicesCatalogue.zoneLabel'),
                    selected: t('multiselect.selected', { count: selectedZones.length }),
                    placeholder: t('servicesCatalogue.zonePlaceholder'),
                    searchPlaceholder: t('multiselect.searchPlaceholder'),
                    noResults: t('multiselect.noResults'),
                    selectAll: t('multiselect.selectAll'),
                    clear: t('multiselect.clear'),
                    apply: t('multiselect.apply'),
                    loading: t('multiselect.loading'),
                  }}
                />
              )}
            </div>

            <div role="region" aria-labelledby="categories-heading">
              <h2 id="categories-heading" className="text-xl font-bold pb-4">
                {t('servicesCatalogue.categoriesTitle')}
              </h2>

              {servicesCategoriesLoading ? (
                <div className="space-y-4 animate-pulse py-3" aria-hidden="true">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-8 bg-muted rounded w-3/4" />
                  ))}
                </div>
              ) : servicesCategoriesError ? (
                <p className="text-sm text-red-500 py-4">{t('common.errorMessage')}</p>
              ) : categoriesWithServices.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">{t('servicesCatalogue.noCategoriesWithServices')}</p>
              ) : (
                <nav aria-label={t('servicesCatalogue.categoriesTitle')}>
                  {categoriesWithServices?.map(
                    (category) =>
                      category.services.length > 0 && (
                        <button
                          key={category.documentId}
                          className={cn(
                            'relative py-3 w-full text-start border-b ps-4',
                            category.documentId === activeTab && 'bg-blue-50',
                          )}
                          onClick={() => handleTabChange(category.documentId)}
                          aria-current={category.documentId === activeTab ? 'page' : undefined}
                        >
                          {category.documentId === activeTab && (
                            <span className="absolute top-0 bottom-0 w-1 bg-link start-0" aria-hidden="true" />
                          )}
                          <span className={`text-link ${category.documentId === activeTab && 'font-bold'}`}>
                            {category.title}
                          </span>
                        </button>
                      ),
                  )}
                </nav>
              )}
            </div>
          </aside>

          <section aria-labelledby="services-heading">
            <div className="flex-1 p-0 md:p-6">
              {servicesCategoriesLoading ? (
                <div className="space-y-4 animate-pulse" aria-hidden="true">
                  <div className="h-6 bg-muted rounded w-1/3" />
                  <div className="rounded-xl bg-white shadow-md p-6 space-y-3">
                    <div className="h-5 bg-muted rounded w-1/2" />
                    <div className="h-4 bg-muted rounded w-full" />
                    <div className="h-4 bg-muted rounded w-5/6" />
                  </div>
                </div>
              ) : servicesCategoriesError ? (
                <p className="text-red-500 text-sm">{t('common.errorMessage')}</p>
              ) : (
                servicesCategories?.map(
                  (category) =>
                    category.documentId === activeTab && (
                      <div key={category.documentId}>
                        <div className="space-y-4 shadow-md p-6 rounded-xl">
                          <h3 className="text-2xl font-semibold text-primary capitalize">{category.title}</h3>
                          <p className="text-foreground mt-4 mb-6">{category.description}</p>
                          <ul>
                            {category.services.length > 0 ? (
                              category.services.map((s) => (
                                <li key={s.documentId} className="mb-3">
                                  <Link
                                    to={'/services/services-catalogue/$id'}
                                    params={{ id: s.documentId }}
                                    className="flex items-center gap-2 text-link underline underline-offset-4 md:underline-offset-8 hover:text-link/80 text-sm capitalize"
                                    aria-label={s.title}
                                  >
                                    <ArrowRight className="shrink-0 rtl:rotate-180 transform" aria-hidden="true" />

                                    {s.title}
                                  </Link>
                                </li>
                              ))
                            ) : (
                              <li className="text-sm text-muted-foreground">{t('servicesCatalogue.noServices')}</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    ),
                )
              )}
            </div>
          </section>
        </div>
      </main>
    </>
  );
}
